import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";
import { useState } from "react";

import { CheckMark, ChevronDown, ChevronUp } from "@/assets/icons";
import { generateValidClassNameHandler } from "@/lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> { hasError?: boolean }

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    SelectTriggerProps
>(({
    children,
    className,
    hasError,
    ...props
}, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <SelectPrimitive.Trigger
            ref={ref}
            className={generateValidClassNameHandler(
                "group w-full text-left text-xs md:text-sm md:min-w-[250px] flex h-10 items-center justify-between rounded border border-gray-300 bg-gray-50 px-3 py-2 placeholder:text-gray-500 focus:outline-none focus:border-primary focus:border disabled:cursor-not-allowed disabled:opacity-30 [&>span]:line-clamp-1",
                "data-[state=open]:border-primary data-[state=open]:border",
                hasError && "border-danger data-[state=open]:border-danger focus:border-danger",
                className,
            )}
            onBlur={() => setIsFocused(false)}
            onFocus={() => setIsFocused(true)}
            onPointerDown={(e) => e.stopPropagation()}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDown
                    size={16}
                    className={generateValidClassNameHandler(
                        "transition-all duration-300 group-[[data-state=open]]:text-primary group-data-[state=open]:rotate-180",
                        isFocused && "text-primary",
                        hasError && "text-danger group-[[data-state=open]]:text-danger",
                        !isFocused && !hasError && "text-gray-500",
                    )}
                />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({
    className,
    ...props
}, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={generateValidClassNameHandler(
            "flex cursor-default items-center justify-center py-1",
            className,
        )}
        {...props}
    >
        <ChevronUp size={16} />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({
    className,
    ...props
}, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={generateValidClassNameHandler(
            "flex cursor-default items-center justify-center py-1",
            className,
        )}
        {...props}
    >
        <ChevronDown size={16} />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({
    children,
    className,
    position = "popper",
    ...props
}, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            position={position}
            ref={ref}
            className={generateValidClassNameHandler(
                "relative z-50 max-h-60 min-w-[8rem] overflow-hidden rounded border bg-card shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
                className,
            )}
            {...props}
        >
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
                className={generateValidClassNameHandler(
                    "p-1",
                    position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({
    className,
    ...props
}, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={generateValidClassNameHandler(
            "py-1.5 pl-8 pr-2 text-xs md:text-sm font-semibold",
            className,
        )}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({
    children,
    className,
    ...props
}, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={generateValidClassNameHandler(
            "relative flex w-full h-8 cursor-pointer data-[state=checked]:font-medium text-xs md:text-sm hover:bg-primary-foreground select-none items-center rounded py-1.5 pl-8 pr-2 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            className,
        )}
        {...props}
    >
        <span className="absolute left-2 flex size-3.5 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
                <CheckMark size={16} />
            </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({
    className,
    ...props
}, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={generateValidClassNameHandler(
            "-mx-1 my-1 h-px bg-gray-300",
            className,
        )}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};
